import {
    esc, mdToHtml, truncAddr,
    buildReportToolbar, buildReportMasthead, buildReportShell, wrapReportSection,
    wrapReportHtml, openReportHtmlInNewTab,
    type ReportTocEntry,
} from '../../utils/reportCommon';

export function generateHolisticTraceReportHtml(
    chain: string | null,
    addresses: string[],
    results: Record<string, any>,
    dataMode?: 'mock' | 'cached' | 'live',
    addressLabels?: Record<string, string>,
    canvasScreenshot?: string
): string {
    const crossLinks: number = results.cross_link_discovery?.cross_links_found ?? 0;
    const sharedEntitiesRaw: any[] = results.shared_entity_identification?.shared_entities ?? [];
    const narrative: string = results.ai_analysis?.narrative ?? '';
    const shallow = results.parallel_shallow_trace ?? {};

    const seedSet = new Set(addresses);
    const labelOf = (a: string): string => (addressLabels && addressLabels[a]) || '';
    const labelToSeed = new Map<string, string>();
    if (addressLabels) {
        for (const [addr, lbl] of Object.entries(addressLabels)) {
            if (seedSet.has(addr)) labelToSeed.set(lbl, addr);
        }
    }

    // Defensive frontend filter — drop any "shared entity" whose name matches a
    // seed's custom label. Subject aliases on seeds shouldn't count as shared
    // cash-out points (one seed appearing in another's trace = cross-link, not
    // a third-party shared entity). Backend should also filter this; the guard
    // here is belt-and-braces.
    const sharedEntities = sharedEntitiesRaw.filter(
        (e: any) => !labelToSeed.has(e.entity_name)
    );

    const chainLabel = chain || 'Unknown';
    const title = 'Holistic Multi-Node Trace Report';
    const subtitle = `Blockchain: ${chainLabel} · ${addresses.length} Target Addresses`;
    const filename = `holistic_trace_${chainLabel}_${new Date().toISOString().slice(0, 10)}`;

    const watermark = dataMode === 'mock' || dataMode === 'cached'
        ? `<div class="demo-watermark">DEMO DATA — NOT FOR EVIDENCE USE (source: ${esc(dataMode || 'mock')})</div>`
        : '';

    // Target Addresses — adds Label column when any seed has a label.
    const anyLabelled = addresses.some(a => labelOf(a));
    const addressRows = addresses.map((a, i) => {
        const lbl = labelOf(a);
        const labelCell = anyLabelled
            ? `<td>${lbl ? `<strong>${esc(lbl)}</strong>` : '<span class="muted">—</span>'}</td>`
            : '';
        return `<tr>
            <td style="text-align:center;">${i + 1}</td>
            ${labelCell}
            <td class="mono">${esc(truncAddr(a))}</td>
            <td class="mono muted" style="font-size:10px;">${esc(a)}</td>
        </tr>`;
    }).join('');

    const targetHeader = anyLabelled
        ? `<thead><tr><th style="width:32px;">#</th><th style="width:120px;">Label</th><th style="width:160px;">Address (short)</th><th>Full Address</th></tr></thead>`
        : `<thead><tr><th style="width:32px;">#</th><th style="width:160px;">Address (short)</th><th>Full Address</th></tr></thead>`;

    // Shared Entities — render connected targets as compact label/address chips
    // on a single line per row instead of stacked addresses.
    const sharedEntityRows = sharedEntities.map((e: any) => {
        const seeds: string[] = e.connected_seeds ?? [];
        const seedDisplay = seeds.map((s: string) => {
            const lbl = labelOf(s);
            const text = lbl || truncAddr(s);
            return `<span title="${esc(s)}" style="display:inline-block;padding:1px 6px;margin:1px 2px 1px 0;background:#eef2ff;color:#3730a3;border:1px solid #c7d2fe;border-radius:3px;font-size:11px;">${esc(text)}</span>`;
        }).join('');
        return `<tr>
            <td><strong>${esc(e.entity_name ?? '—')}</strong></td>
            <td>${seedDisplay}</td>
            <td style="text-align:center;">${e.seed_count} / ${addresses.length}</td>
        </tr>`;
    }).join('');

    const metricsRows = [
        `<tr><td style="width:200px;">Cross-Links Found</td><td><strong>${crossLinks}</strong> direct transactions between target addresses</td></tr>`,
        `<tr><td>Shared Entities</td><td><strong>${sharedEntities.length}</strong> common funding or cash-out entities${sharedEntities.length !== sharedEntitiesRaw.length ? ` <span class="muted">(${sharedEntitiesRaw.length - sharedEntities.length} seed-alias rows filtered)</span>` : ''}</td></tr>`,
        shallow.traces_attempted != null
            ? `<tr><td>Shallow Traces</td><td>${shallow.traces_attempted} attempted · ${shallow.traces_succeeded ?? 0} succeeded · ${shallow.traces_failed ?? 0} failed</td></tr>`
            : '',
    ].join('');

    const toc: ReportTocEntry[] = [
        { id: 'targets', label: '1. Target Addresses' },
        { id: 'metrics', label: '2. Discovery Metrics' },
        { id: 'shared-entities', label: '3. Shared Entities' },
    ];
    if (canvasScreenshot) toc.push({ id: 'graph', label: '4. Graph Visualization' });
    if (narrative) toc.push({ id: 'ai-summary', label: `${canvasScreenshot ? '5' : '4'}. AI Summary` });

    let main = buildReportMasthead({
        title,
        subtitle,
        dataMode: dataMode || undefined,
    });

    main += wrapReportSection('targets', `1. Target Addresses (${addresses.length})`,
        `<table class="data" style="table-layout:fixed;">${targetHeader}<tbody>${addressRows}</tbody></table>`);

    main += wrapReportSection('metrics', '2. Discovery Metrics',
        `<table class="data"><tbody>${metricsRows}</tbody></table>`);

    main += wrapReportSection('shared-entities', '3. Shared Entities',
        sharedEntities.length > 0
            ? `<table class="data" style="table-layout:fixed;">
                <colgroup>
                    <col style="width:42%;"/>
                    <col style="width:43%;"/>
                    <col style="width:15%;"/>
                </colgroup>
                <thead><tr><th>Entity</th><th>Connected Targets</th><th style="text-align:center;">Coverage</th></tr></thead>
                <tbody>${sharedEntityRows}</tbody>
               </table>`
            : `<p class="muted" style="font-style:italic;font-size:12px;margin-top:8px;">
                No shared entities identified across the traced paths.
               </p>`);

    if (canvasScreenshot) {
        main += wrapReportSection('graph', '4. Graph Visualization',
            `<div class="canvas-snapshot-wrap">
                <img src="${canvasScreenshot}" alt="Holistic trace canvas snapshot" />
            </div>
            <p style="font-size:10px;color:#94a3b8;text-align:center;margin-top:6px;">Amber edges mark cross-links between target addresses; grey edges are BFS shallow-trace context. Edge thickness reflects transaction magnitude.</p>`,
        );
    }

    if (narrative) {
        main += wrapReportSection('ai-summary', `${canvasScreenshot ? '5' : '4'}. AI Investigation Summary`,
            `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:16px;font-size:13px;line-height:1.6;">
                ${mdToHtml(narrative)}
            </div>`);
    }

    main += `<div style="text-align:center;margin-top:40px;padding-top:20px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;">
        Generated by Outris Crypto Investigation Platform
    </div>`;

    // Print-only CSS: graph snapshot starts on its own page, fits within
    // one A4 page (margins set in REPORT_CSS @page = 20mm/15mm/25mm/15mm
    // → ~252mm vertical print area; we cap image height at ~210mm so the
    // h2 + caption have room and the section never spills onto page 2).
    // On screen the image renders inline at full width as before.
    const extraCss = `
        @media print {
            #graph { page-break-before: always; break-before: page; page-break-inside: avoid; break-inside: avoid; }
            #graph img { max-height: 210mm; max-width: 100%; object-fit: contain; }
        }
        @media screen {
            .canvas-snapshot-wrap img { max-width: 100%; max-height: 75vh; object-fit: contain; border: 1px solid #e2e8f0; border-radius: 4px; }
        }
        .canvas-snapshot-wrap { text-align: center; margin: 12px 0; }
        .demo-watermark {
            background: #fef3c7; border: 2px solid #f59e0b; border-radius: 6px;
            padding: 10px 24px; margin: 8px 24px; text-align: center;
            font-weight: 700; font-size: 14px; color: #92400e;
            letter-spacing: 1px; text-transform: uppercase;
        }
        @media print { .demo-watermark { display: none !important; } }
    `;

    return wrapReportHtml(
        watermark + buildReportToolbar(title, filename) + buildReportShell(main, toc),
        title,
        extraCss,
    );
}

export function openHolisticReportInNewTab(
    chain: string | null,
    addresses: string[],
    results: Record<string, any>,
    dataMode?: 'mock' | 'cached' | 'live',
    addressLabels?: Record<string, string>,
    canvasScreenshot?: string,
    /**
     * `options.onPersist` is invoked with the rendered HTML so the caller can
     * archive the report under the case/investigation (same contract as the
     * Auto-Investigate and Follow-the-Money reports). The report still opens in
     * a new tab regardless of whether persistence succeeds. Without it, Holistic
     * reports opened but were never persisted to the Case module (ISSUE-02).
     */
    options?: { onPersist?: (html: string) => void },
) {
    const html = generateHolisticTraceReportHtml(chain, addresses, results, dataMode, addressLabels, canvasScreenshot);
    openReportHtmlInNewTab(html);
    try {
        options?.onPersist?.(html);
    } catch (e) {
        console.warn('Holistic report onPersist threw:', e);
    }
}
